import { useMediaQuery } from '@mantine/hooks';
import { IconArrowRight } from '@tabler/icons-react';
import {
  BenefitStatus,
  BenefitType,
  CoverageChange,
  EnrollmentTag,
  InsuredPeopleDto,
  PlanMarket,
  PolicyType,
  SelfEnrollType,
} from '@zorro/clients';
import { Button } from '@zorro/design-system/v2/Button';
import { Card } from '@zorro/design-system/v2/Card';
import { Divider } from '@zorro/design-system/v2/Divider';
import { Link } from '@zorro/design-system/v2/Link';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { formatCurrencyEnUs } from '@zorro/shared/formatters';
import { Box, Group, Icon, Stack, Text } from '@zorro/web/design-system';
import {
  EnrollmentAction,
  EnrollmentTagBadge,
  sortEnrollmentTags,
} from '@zorro/web/shared-ui';
import { useAppRouter, useRoles, useTextOverflow } from '@zorro/web/ui-utils';
import { Ref } from 'react';
import { $enum } from 'ts-enum-util';

import { CarrierLogoNext } from '../../../../CarrierLogoNext';
import { CopyableTooltip } from '../../../../CopyableTooltip';
import {
  PlanMarketBadge,
  PolicyTypeBadge,
  RenewalBadge,
  SelfEnrollBadge,
} from '../../../../PlanBadge';
import { EmployeePagePlansBenefitCardActionsMenu } from './EmployeePagePlansBenefitCardActionsMenu';
import { EmployeePagePlansBenefitCardActionsRequired } from './EmployeePagePlansBenefitCardActionsRequired';
import { EmployeePagePlansBenefitCardStatus } from './EmployeePagePlansBenefitCardStatus';
import { getInsuredNames } from './EmployeePagePlansUtils';

const PlanName = ({
  name,
  benefitsSummaryUrl,
  isBiggerThanXl,
  textRef,
}: {
  name: string | null;
  benefitsSummaryUrl?: string | null;
  isBiggerThanXl: boolean;
  textRef?: Ref<HTMLSpanElement & HTMLDivElement>;
}) => (
  <Box
    // Do not change these maw values until the plan name component is changed to be dynamic
    maw={isBiggerThanXl ? '50rem' : { base: '12rem', lg: '15rem', xl: '25rem' }}
  >
    {benefitsSummaryUrl ? (
      <Link
        labelRef={textRef}
        href={benefitsSummaryUrl}
        variant="tertiary"
        target="_blank"
        shouldTruncate
      >
        {name}
      </Link>
    ) : (
      <Text ref={textRef} truncate="end" w="100%">
        {name}
      </Text>
    )}
  </Box>
);

type Props = {
  onboardingPeriodId: string;
  employerId: string;
  employeeId: string;
  carrierName: string;
  benefitId: string;
  memberId?: string | null;
  planId: string | null;
  benefitType: BenefitType;
  name: string | null;
  isDon: boolean;
  benefitsSummaryUrl?: string | null;
  premium: number;
  status?: BenefitStatus | null;
  insuredPeople?: InsuredPeopleDto | null;
  selfEnroll?: SelfEnrollType | null;
  planMarket?: PlanMarket | null;
  policyType?: PolicyType | null;
  coverageChange?: CoverageChange;
  isCombinedPlan?: boolean;
  extendedPlanDetails?: string | null;
  enrollmentTags?: EnrollmentTag[];
  onEnrollmentTabActionChange: (action?: EnrollmentAction) => void;
};

export const EmployeePagePlansBenefitCard = ({
  onboardingPeriodId,
  employerId,
  employeeId,
  benefitId,
  memberId,
  planId,
  benefitsSummaryUrl,
  premium,
  benefitType,
  carrierName,
  name,
  status,
  isDon,
  coverageChange,
  insuredPeople,
  planMarket,
  selfEnroll,
  policyType,
  isCombinedPlan,
  extendedPlanDetails,
  enrollmentTags,
  onEnrollmentTabActionChange,
}: Props) => {
  const { navigateToRouteAsync } = useAppRouter();
  const { isManagement } = useRoles();
  const isBiggerThanXl = useMediaQuery(`(min-width: 120rem)`);
  const { isTextOverflowing, textRef } = useTextOverflow<
    HTMLSpanElement & HTMLDivElement
  >(name ?? undefined);

  const insuredNames = insuredPeople ? getInsuredNames(insuredPeople) : [];

  const handleStartApplication = async () => {
    await navigateToRouteAsync(
      'OPS_EMPLOYEE_PLANS_TAB',
      [{ onboardingPeriodId, employerId, employeeId }],
      { benefitId }
    );
    onEnrollmentTabActionChange(EnrollmentAction.CARRIER_APPLICATION);
  };

  const handleConfirmEnrollment = async () => {
    await navigateToRouteAsync(
      'OPS_EMPLOYEE_PLANS_TAB',
      [{ onboardingPeriodId, employerId, employeeId }],
      { benefitId }
    );
    onEnrollmentTabActionChange(EnrollmentAction.ENROLLMENT_CONFIRMATION);
  };

  const actionButton = $enum
    .mapValue(status)
    .with<
      { label: string; onClick?: () => void; tooltipLabel?: string } | undefined
    >({
      [BenefitStatus.AWAITING_MEDICAL]: {
        label: 'Start application',
        tooltipLabel:
          'Blocked for application until the medical plan that includes the employee is submitted to the carrier',
      },
      [BenefitStatus.READY_TO_APPLY]: !isDon
        ? {
            label: 'Start application',
            onClick: handleStartApplication,
          }
        : undefined,
      [BenefitStatus.CARRIER_APPLICATION_SENT]: !isDon
        ? {
            label: 'Confirm enrollment',
            onClick: handleConfirmEnrollment,
          }
        : undefined,
      [BenefitStatus.ENROLLMENT_CONFIRMED]: undefined,
      [BenefitStatus.IN_CART]: undefined,
      [BenefitStatus.ACTIVE]: undefined,
      [BenefitStatus.ENDED]: undefined,
      [BenefitStatus.AWAITING_EMPLOYEE]: undefined,
      [BenefitStatus.AWAITING_PAYMENT]: undefined,
      [$enum.handleUnexpected]: undefined,
      [$enum.handleUndefined]: undefined,
      [$enum.handleNull]: undefined,
    });

  return (
    <Card variant="white-shadow" p="xl">
      <Stack>
        <Group w="100%" wrap="nowrap" align="start">
          <Card
            variant="white-shadow"
            pos="relative"
            mih="3.75rem"
            miw="7.5rem"
            bdrs="sm"
          >
            <CarrierLogoNext
              style={{ paddingBlock: '0.5rem', paddingInline: '15%' }}
              plan={{ carrierName }}
              sizes="120px"
            />
          </Card>

          <Stack gap="xs" w="100%">
            <Group wrap="nowrap" w="100%" justify="space-between">
              {planId ? (
                <CopyableTooltip>
                  <CopyableTooltip.Target>
                    <PlanName
                      name={name}
                      benefitsSummaryUrl={benefitsSummaryUrl}
                      isBiggerThanXl={isBiggerThanXl}
                      textRef={textRef}
                    />
                  </CopyableTooltip.Target>
                  <CopyableTooltip.Content>
                    {isTextOverflowing && name && (
                      <CopyableTooltip.Item>
                        <CopyableTooltip.Label>Plan name</CopyableTooltip.Label>
                        <CopyableTooltip.Value>{name}</CopyableTooltip.Value>
                      </CopyableTooltip.Item>
                    )}
                    <CopyableTooltip.Item>
                      <CopyableTooltip.Label>Plan ID</CopyableTooltip.Label>
                      <CopyableTooltip.Value>{planId}</CopyableTooltip.Value>
                      <CopyableTooltip.Copy
                        text={planId}
                        successMessage="Plan ID copied"
                      />
                    </CopyableTooltip.Item>
                  </CopyableTooltip.Content>
                </CopyableTooltip>
              ) : (
                <PlanName
                  name={name}
                  benefitsSummaryUrl={benefitsSummaryUrl}
                  isBiggerThanXl={isBiggerThanXl}
                />
              )}
              <EmployeePagePlansBenefitCardStatus status={status} />
            </Group>
            <Stack gap="xs">
              <Group wrap="nowrap">
                <Group gap="xxs" align="baseline">
                  <Text size="sm" c="var(--color-text-default-secondary)">
                    Premium
                  </Text>
                  <Text size="sm">{formatCurrencyEnUs(premium)}</Text>
                </Group>
                <Group gap="xxs" align="baseline">
                  <Text size="sm" c="var(--color-text-default-secondary)">
                    Covered
                  </Text>
                  <Text size="sm">{insuredNames.join(', ')}</Text>
                </Group>
                {memberId && (
                  <Group gap="xxs" align="baseline">
                    <Text size="sm" c="var(--color-text-default-secondary)">
                      Member ID
                    </Text>
                    <Text size="sm">{memberId}</Text>
                  </Group>
                )}
              </Group>
              {isCombinedPlan && extendedPlanDetails && (
                <Group gap="xxs" align="baseline">
                  <Text size="sm" c="var(--color-text-default-secondary)">
                    Combined plan info:
                  </Text>
                  <Text size="sm"> {extendedPlanDetails}</Text>
                </Group>
              )}
              <Group gap="xs">
                <PlanMarketBadge planMarket={planMarket} />
                <SelfEnrollBadge selfEnroll={selfEnroll} />
                <PolicyTypeBadge policyType={policyType} />
                <RenewalBadge coverageChange={coverageChange} />
                {isManagement &&
                  sortEnrollmentTags(enrollmentTags).map((tag) => (
                    <EnrollmentTagBadge key={tag} tag={tag} />
                  ))}
              </Group>
            </Stack>
          </Stack>
        </Group>
        <Divider />
        <Group gap="sm" justify="end">
          {benefitType === BenefitType.MAJOR_MEDICAL && (
            <EmployeePagePlansBenefitCardActionsRequired
              onboardingPeriodId={onboardingPeriodId}
              employerId={employerId}
            />
          )}
          <EmployeePagePlansBenefitCardActionsMenu
            onboardingPeriodId={onboardingPeriodId}
            benefitType={benefitType}
            employeeId={employeeId}
            employerId={employerId}
            benefitId={benefitId}
            status={status}
            isDon={isDon}
            onEnrollmentTabActionChange={onEnrollmentTabActionChange}
          />
          {actionButton && (
            <Tooltip
              label={actionButton.tooltipLabel}
              disabled={!actionButton.tooltipLabel}
            >
              <Box>
                <Button
                  disabled={!actionButton.onClick}
                  rightSection={
                    <Icon
                      icon={IconArrowRight}
                      color={
                        !actionButton.onClick
                          ? 'var(--color-text-disabled-primary)'
                          : 'var(--color-text-on-primary)'
                      }
                    />
                  }
                  onClick={actionButton.onClick}
                >
                  {actionButton.label}
                </Button>
              </Box>
            </Tooltip>
          )}
        </Group>
      </Stack>
    </Card>
  );
};
